// TarotReadingTab.tsx
import React, { useState } from "react";
import { Layers } from "lucide-react";
import AstriaVietnamShell, {
  SHELL_COLORS_ASTRIA_VIETNAM,
} from "../AstriaVietnamShell";
import {
  AstriaVietnamResultCard,
  AstriaVietnamFieldBlock,
  AstriaVietnamLoadingState,
  AstriaVietnamErrorState,
  AstriaVietnamChatCta,
} from "../AstriaVietnamCard";
import {
  WizardStep,
  WizardNav,
  WizardDropdown,
} from "../AstriaVietnamWizard";
import { useAstriaVietnamTab } from "../useAstriaVietnamTab";
import { AstriaVietnamTarotData } from "../../../../contexts/ChatContext";
import { useAstriaVietnamLanguage } from "../astriaVietnamLanguage";
import { ASTRIA_VIETNAM_COPY } from "../astriaVietnamCopy";

// SubCategory name must contain "Tarot" — matches
// resolveVietnamSubcategoryEntry in
// backend/helper/vietnam/astriaVietnamPromptService.js.
const SUBCATEGORY_NAME = "Tarot Reading";

const TarotReadingTab: React.FC = () => {
  const [lang, setLang] = useAstriaVietnamLanguage();
  const copy = ASTRIA_VIETNAM_COPY[lang];
  const t = copy.tarot;
  const shared = copy.shared;

  const { loading, error, message, send, reset, goToChat } =
    useAstriaVietnamTab({ subCategoryName: SUBCATEGORY_NAME, language: lang });

  const [topic, setTopic] = useState<string | null>(null);

  const started = !!message || loading || !!error;
  const step1Valid = !!topic;

  const handleSubmit = () => {
    send("Draw a tarot card and reflect gently on it for me.", {
      tarot_topic: topic || undefined,
    });
  };

  const tarotData = message?.astriaVietnamData as AstriaVietnamTarotData | undefined;

  if (started) {
    return (
      <AstriaVietnamShell active="tarot" lang={lang} onLangChange={setLang}>
        <div style={{ marginBottom: 20 }}>
          <div
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: SHELL_COLORS_ASTRIA_VIETNAM.titleColor,
              marginBottom: 4,
            }}
          >
            {t.tabLabel}
          </div>
          <p style={{ fontSize: 13.5, color: SHELL_COLORS_ASTRIA_VIETNAM.mutedColor }}>
            {t.tabSublabel} — {t.resultTitle}
          </p>
        </div>

        {loading && <AstriaVietnamLoadingState label={t.loading} />}

        {!loading && error && (
          <AstriaVietnamErrorState
            message={error}
            onRetry={handleSubmit}
            retryLabel={shared.tryAgain}
          />
        )}

        {!loading && !error && tarotData && (
          <>
            <AstriaVietnamResultCard
              icon={<Layers className="w-5 h-5" />}
              title={tarotData.card_name || t.resultTitle}
              subtitle={t.tabLabel}
              shareText={`${tarotData.card_name}\n\n${tarotData.card_meaning}\n\n${tarotData.reflection}`}
            >
              <AstriaVietnamFieldBlock label={t.fields.cardMeaning} value={tarotData.card_meaning} />
              <AstriaVietnamFieldBlock label={t.fields.reflection} value={tarotData.reflection} />
              <AstriaVietnamFieldBlock label={t.fields.guidance} value={tarotData.guidance} />
            </AstriaVietnamResultCard>
            <button
              onClick={() => {
                reset();
                setTopic(null);
              }}
              className="text-xs font-semibold transition-colors"
              style={{ marginTop: 14, color: SHELL_COLORS_ASTRIA_VIETNAM.mutedColor }}
            >
              {shared.startNewReading}
            </button>
            <AstriaVietnamChatCta onClick={goToChat} label={shared.continueInChat} />
          </>
        )}

        {!loading && !error && !tarotData && message && (
          <AstriaVietnamResultCard
            icon={<Layers className="w-5 h-5" />}
            title={t.resultTitle}
            subtitle={t.tabLabel}
          >
            <p
              style={{
                fontSize: 15,
                lineHeight: 1.75,
                color: SHELL_COLORS_ASTRIA_VIETNAM.bodyColor,
                whiteSpace: "pre-wrap",
              }}
            >
              {message.content}
            </p>
          </AstriaVietnamResultCard>
        )}
      </AstriaVietnamShell>
    );
  }

  return (
    <AstriaVietnamShell active="tarot" lang={lang} onLangChange={setLang}>
      <WizardStep stepKey="details" question={t.step1Question}>
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: SHELL_COLORS_ASTRIA_VIETNAM.mutedColor,
            marginBottom: 8,
          }}
        >
          {t.topicLabel}
        </div>
        <WizardDropdown
          options={t.topicOptions}
          value={topic}
          onChange={setTopic}
          placeholder={t.topicPlaceholder}
        />

        <WizardNav
          showBack={false}
          nextDisabled={!step1Valid}
          onNext={handleSubmit}
          nextLabel={t.revealButton}
          backLabel={shared.back}
        />
      </WizardStep>
    </AstriaVietnamShell>
  );
};

export default TarotReadingTab;
